<template>
  <div class="presales-submissions">
    <EntraPageHeader
      :title="submissionId ? 'Presales Handover' : 'Presales Handovers'"
      :showTenantInfo="false"
      :showBreadcrumb="false"
      :showSearch="false"
      :showSavedViews="false"
      :showExport="false"
      :showRefresh="true"
      @refresh="loadData"
    />

    <div class="page-body">
      <div v-if="error" class="error-message" role="alert">{{ error }}</div>

      <!-- ===== Detail ===== -->
      <template v-if="submissionId">
        <router-link to="/presales/submissions" class="back-link">
          <svg viewBox="0 0 20 20" fill="currentColor" class="back-icon">
            <path fill-rule="evenodd" d="M17 10a.75.75 0 01-.75.75H5.61l4.04 3.79a.75.75 0 11-1.04 1.08l-5.25-4.92a.75.75 0 010-1.08l5.25-4.92a.75.75 0 111.04 1.08L5.61 9.25h10.64A.75.75 0 0117 10z" clip-rule="evenodd"/>
          </svg>
          Back to submissions
        </router-link>

        <div v-if="loading" class="loading-state">
          <span class="spinner" aria-hidden="true"></span> Loading submission...
        </div>

        <template v-if="submission">
          <div class="card detail-header-card">
            <div class="detail-identity">
              <div class="avatar" aria-hidden="true">{{ customerInitials }}</div>
              <div>
                <h3 class="detail-name">{{ submission.customerName }}</h3>
                <div class="detail-meta">
                  <code class="client-code">{{ submission.dbTicket }}</code>
                  <span class="meta-dot" aria-hidden="true"></span>
                  <span class="muted">
                    Submitted {{ formatDate(submission.createdAt) }}<template v-if="submission.createdByName"> by {{ submission.createdByName }}</template>
                  </span>
                </div>
              </div>
            </div>
            <div class="detail-actions">
              <button
                v-if="onboardingBoard"
                type="button"
                class="primary-button"
                @click="viewOnboardingBoard"
              >
                View onboarding board
              </button>
              <button
                v-else
                type="button"
                class="primary-button"
                :disabled="startingOnboarding"
                @click="startOnboarding"
              >
                {{ startingOnboarding ? 'Starting...' : 'Start onboarding' }}
              </button>
              <button v-if="!confirmingDelete" type="button" class="danger-button" @click="confirmingDelete = true">Delete</button>
              <template v-else>
                <span class="confirm-text">Delete this handover?</span>
                <button type="button" class="danger-button" :disabled="deleting" @click="deleteSubmission">
                  {{ deleting ? 'Deleting...' : 'Confirm' }}
                </button>
                <button type="button" class="secondary-button" @click="confirmingDelete = false">Cancel</button>
              </template>
            </div>
          </div>

          <div class="detail-layout">
            <aside class="panel-nav">
              <button
                type="button"
                class="panel-nav-item"
                :class="{ 'panel-nav-active': activePanel === 'overview' }"
                @click="activePanel = 'overview'"
              >
                <span class="panel-nav-title">Overview</span>
              </button>
              <button
                v-for="section in answerSections"
                :key="section.id"
                type="button"
                class="panel-nav-item"
                :class="{ 'panel-nav-active': activePanel === section.id }"
                @click="activePanel = section.id"
              >
                <span class="panel-nav-title">{{ section.title }}</span>
                <span class="panel-nav-count">{{ section.entries.length }}</span>
              </button>
            </aside>

            <div class="panel-content">
          <div v-if="activePanel === 'overview' && submission.derived" class="card">
            <h3 class="card-title">Derived Onboarding Configuration</h3>
            <div class="derived-grid">
              <div class="derived-tile">
                <div class="derived-icon icon-indigo" aria-hidden="true">
                  <svg viewBox="0 0 20 20" fill="currentColor"><path d="M10 2a2 2 0 00-2 2v1H5a2 2 0 00-2 2v9a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-3V4a2 2 0 00-2-2zM8 4h4v1H8V4z"/></svg>
                </div>
                <div class="derived-body">
                  <span class="derived-label">M365 Deployment</span>
                  <span class="chip" :class="submission.derived.m365DeploymentType === 'greenfield' ? 'chip-new' : 'chip-existing'">
                    {{ submission.derived.m365DeploymentType || 'n/a' }}
                  </span>
                </div>
              </div>
              <div class="derived-tile">
                <div class="derived-icon icon-azure" aria-hidden="true">
                  <svg viewBox="0 0 20 20" fill="currentColor"><path d="M11 2L4 12h5l-1 6 8-11h-5l0-5z"/></svg>
                </div>
                <div class="derived-body">
                  <span class="derived-label">Azure Pathway</span>
                  <span v-if="submission.derived.azurePathway" class="chip" :class="submission.derived.azurePathway === 'new' ? 'chip-new' : 'chip-existing'">
                    {{ submission.derived.azurePathway === 'new' ? 'New build' : 'Existing takeover' }}
                  </span>
                  <span v-else class="chip chip-na">Not in scope</span>
                </div>
              </div>
              <div class="derived-tile">
                <div class="derived-icon icon-aws" aria-hidden="true">
                  <svg viewBox="0 0 20 20" fill="currentColor"><path d="M6 6a4 4 0 118 0 3.5 3.5 0 01-.5 7H6a3.5 3.5 0 010-7z"/></svg>
                </div>
                <div class="derived-body">
                  <span class="derived-label">AWS Pathway</span>
                  <span v-if="submission.derived.awsPathway" class="chip" :class="submission.derived.awsPathway === 'new' ? 'chip-new' : 'chip-existing'">
                    {{ submission.derived.awsPathway === 'new' ? 'New build' : 'Existing takeover' }}
                  </span>
                  <span v-else class="chip chip-na">Not in scope</span>
                </div>
              </div>
              <div class="derived-tile">
                <div class="derived-icon icon-cloud" aria-hidden="true">
                  <svg viewBox="0 0 20 20" fill="currentColor"><path d="M4 15a3 3 0 01.6-5.95A5 5 0 0114.3 8.6 3.75 3.75 0 0115.5 15H4z"/></svg>
                </div>
                <div class="derived-body">
                  <span class="derived-label">Options Cloud</span>
                  <span class="chip" :class="submission.derived.optionsCloud ? 'chip-new' : 'chip-na'">
                    {{ submission.derived.optionsCloud ? 'In scope' : 'Not in scope' }}
                  </span>
                </div>
              </div>
            </div>

            <h4 class="card-subtitle">
              Conditional Onboarding Tasks
              <span class="task-count">{{ includedTaskCount }} of {{ submission.derived.conditionalTasks.length }} included</span>
            </h4>
            <div class="task-grid">
              <div v-for="task in submission.derived.conditionalTasks" :key="task.id" class="task-row" :class="{ 'task-excluded': !task.included }">
                <svg v-if="task.included" class="task-icon task-icon-in" viewBox="0 0 20 20" fill="currentColor" aria-label="Included">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.7-9.3a1 1 0 00-1.4-1.4L9 10.6 7.7 9.3a1 1 0 00-1.4 1.4l2 2a1 1 0 001.4 0l4-4z" clip-rule="evenodd"/>
                </svg>
                <svg v-else class="task-icon task-icon-out" viewBox="0 0 20 20" fill="currentColor" aria-label="Excluded">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z" clip-rule="evenodd"/>
                </svg>
                <span>{{ task.task }}</span>
              </div>
            </div>
            <p class="muted small">Included tasks are selected by the presales answers; excluded tasks are omitted from the onboarding checklist.</p>
          </div>

          <template v-else-if="activeSection">
            <div class="card">
              <div class="section-card-header">
                <h3 class="card-title">{{ activeSection.title }}</h3>
                <span class="panel-nav-count">{{ activeSection.entries.length }} answered</span>
              </div>
              <div class="answer-rows">
                <div v-for="entry in activeSection.entries" :key="entry.fieldId" class="answer-row">
                  <span class="answer-label">{{ entry.label }}</span>
                  <div class="answer-value">
                    <span v-if="entry.type === 'single'" class="answer-chip">{{ entry.value }}</span>
                    <span v-else-if="entry.type === 'multi'" class="answer-chip-set">
                      <span v-for="item in entry.items" :key="item" class="answer-chip">{{ item }}</span>
                    </span>
                    <p v-else class="answer-text" :class="{ 'answer-text-panel': entry.multiline }">{{ entry.value }}</p>
                  </div>
                </div>
              </div>
            </div>
          </template>
            </div>
          </div>
        </template>
      </template>

      <!-- ===== List ===== -->
      <template v-else>
        <div class="filters-bar">
          <div class="search-wrap">
            <svg class="search-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.48l4.82 4.82a.75.75 0 11-1.06 1.06l-4.82-4.82A6 6 0 012 8z" clip-rule="evenodd"/>
            </svg>
            <input
              v-model="search"
              type="text"
              class="filter-input"
              placeholder="Search customer or client code..."
              @keyup.enter="reloadList"
              aria-label="Search submissions"
            />
          </div>
          <router-link to="/presales/handover" class="primary-button as-link">New Handover</router-link>
        </div>

        <div v-if="loading" class="loading-state">
          <span class="spinner" aria-hidden="true"></span> Loading submissions...
        </div>

        <div v-else-if="items.length === 0" class="card empty-card">
          <svg class="empty-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clip-rule="evenodd"/>
          </svg>
          <h3>No presales handovers yet</h3>
          <p class="muted">Completed handovers drive the onboarding checklist — create the first one to see the derived tasks.</p>
          <router-link to="/presales/handover" class="primary-button as-link">Create a handover</router-link>
        </div>

        <div v-else class="table-wrapper card">
          <table class="data-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>DB Ticket</th>
                <th>Build Profile</th>
                <th>Submitted</th>
                <th class="col-action"><span class="visually-hidden">Open</span></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in items" :key="item.id" class="row-link" @click="openSubmission(item.id)">
                <td>
                  <div class="cell-customer">
                    <span class="avatar avatar-sm" aria-hidden="true">{{ initialsFor(item.customerName) }}</span>
                    <span class="cell-name">{{ item.customerName }}</span>
                  </div>
                </td>
                <td><code class="client-code">{{ item.dbTicket }}</code></td>
                <td>
                  <span v-if="item.buildProfile" class="chip chip-existing">{{ item.buildProfile }}</span>
                  <span v-else class="muted">—</span>
                </td>
                <td class="muted">{{ formatDate(item.createdAt) }}</td>
                <td class="col-action">
                  <router-link :to="'/presales/submissions/' + item.id" class="view-link" @click.stop>View</router-link>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="totalPages > 1" class="pagination">
          <button type="button" class="secondary-button" :disabled="page <= 1" @click="changePage(page - 1)">Previous</button>
          <span class="muted">Page {{ page }} of {{ totalPages }}</span>
          <button type="button" class="secondary-button" :disabled="page >= totalPages" @click="changePage(page + 1)">Next</button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import EntraPageHeader from '@/components/entra/common/EntraPageHeader.vue';
import {
  createOnboardingBoard,
  deletePresalesSubmission,
  getOnboardingBoardBySubmission,
  getPresalesFormDefinition,
  getPresalesSubmission,
  getPresalesSubmissions
} from '@/services/presales-api.js';

export default {
  name: 'PresalesSubmissions',
  components: { EntraPageHeader },
  data() {
    return {
      definition: null,
      items: [],
      submission: null,
      loading: false,
      error: null,
      search: '',
      page: 1,
      pageSize: 20,
      total: 0,
      confirmingDelete: false,
      deleting: false,
      activePanel: 'overview',
      onboardingBoard: null,
      startingOnboarding: false
    };
  },
  computed: {
    submissionId() {
      return this.$route.params.submissionId || null;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.pageSize));
    },
    customerInitials() {
      return this.submission ? this.initialsFor(this.submission.customerName) : '';
    },
    includedTaskCount() {
      if (!this.submission || !this.submission.derived) return 0;
      return this.submission.derived.conditionalTasks.filter((task) => task.included).length;
    },
    activeSection() {
      return this.answerSections.find((section) => section.id === this.activePanel) || null;
    },
    answerSections() {
      if (!this.submission || !this.definition) return [];
      const sections = [];
      sections.push({
        id: 'header',
        title: 'Customer',
        entries: [
          { fieldId: 'customerName', label: 'Customer name', type: 'single', value: this.submission.customerName },
          { fieldId: 'dbTicket', label: 'DB Ticket', type: 'single', value: this.submission.dbTicket }
        ]
      });
      for (const section of this.definition.sections) {
        if (section.id === 'customer') continue;
        const entries = [];
        for (const field of section.fields) {
          const value = this.submission.answers[field.id];
          if (value === undefined || value === null || value === '') continue;
          if (field.type === 'multi') {
            if (!Array.isArray(value) || value.length === 0) continue;
            entries.push({
              fieldId: field.id,
              label: field.label,
              type: 'multi',
              items: value.map((entry) => this.optionLabel(field, entry))
            });
          } else if (field.type === 'single') {
            entries.push({ fieldId: field.id, label: field.label, type: 'single', value: this.optionLabel(field, value) });
          } else {
            entries.push({ fieldId: field.id, label: field.label, type: 'text', value: value, multiline: !!field.multiline });
          }
        }
        if (entries.length > 0) sections.push({ id: section.id, title: section.title, entries: entries });
      }
      return sections;
    }
  },
  watch: {
    '$route.params.submissionId'() {
      this.resetState();
      this.loadData();
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    resetState() {
      this.error = null;
      this.confirmingDelete = false;
    },
    async loadData() {
      this.loading = true;
      this.error = null;
      try {
        if (this.submissionId) {
          const [submission, definition, onboardingBoard] = await Promise.all([
            getPresalesSubmission(this.submissionId),
            this.definition ? Promise.resolve(this.definition) : getPresalesFormDefinition(),
            getOnboardingBoardBySubmission(this.submissionId)
          ]);
          this.submission = submission;
          this.definition = definition;
          this.onboardingBoard = onboardingBoard;
          this.activePanel = 'overview';
        } else {
          const result = await getPresalesSubmissions({
            page: this.page,
            pageSize: this.pageSize,
            search: this.search || undefined
          });
          this.items = result.items;
          this.total = result.total;
        }
      } catch (error) {
        this.error = error.message;
      } finally {
        this.loading = false;
      }
    },
    reloadList() {
      this.page = 1;
      this.loadData();
    },
    changePage(page) {
      this.page = page;
      this.loadData();
    },
    openSubmission(id) {
      this.$router.push('/presales/submissions/' + id);
    },
    async startOnboarding() {
      if (!this.submissionId) return;
      this.startingOnboarding = true;
      this.error = null;
      try {
        const result = await createOnboardingBoard(this.submissionId);
        this.onboardingBoard = result.board;
        this.$router.push('/presales/onboarding/' + result.board.id);
      } catch (error) {
        if (error.code === 'BOARD_EXISTS' && error.details && error.details.boardId) {
          this.$router.push('/presales/onboarding/' + error.details.boardId);
        } else {
          this.error = error.message;
        }
      } finally {
        this.startingOnboarding = false;
      }
    },
    viewOnboardingBoard() {
      if (this.onboardingBoard) {
        this.$router.push('/presales/onboarding/' + this.onboardingBoard.id);
      }
    },
    initialsFor(name) {
      if (!name) return '?';
      return name
        .split(/\s+/)
        .slice(0, 2)
        .map((word) => word.charAt(0).toUpperCase())
        .join('');
    },
    optionLabel(field, value) {
      const option = (field.options || []).find((entry) => entry.value === value);
      return option ? option.label : value;
    },
    formatDate(value) {
      if (!value) return '—';
      return new Date(value).toLocaleString();
    },
    async deleteSubmission() {
      this.deleting = true;
      this.error = null;
      try {
        await deletePresalesSubmission(this.submissionId);
        this.$router.push('/presales/submissions');
      } catch (error) {
        this.error = error.message;
        this.confirmingDelete = false;
      } finally {
        this.deleting = false;
      }
    }
  }
};
</script>

<style scoped>
.presales-submissions {
  min-height: 100vh;
  background: #f8fafc;
}

.page-body {
  padding: 1.5rem 2rem 3rem;
  max-width: 1100px;
}

.card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  padding: 1.5rem;
  margin-bottom: 1.25rem;
}

.card-title {
  margin: 0 0 1rem 0;
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
}

.card-subtitle {
  margin: 1.75rem 0 0.875rem 0;
  font-size: 0.875rem;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
}

.task-count {
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748b;
}

/* ---------- List ---------- */

.filters-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.search-wrap {
  position: relative;
  width: 340px;
}

.search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1rem;
  height: 1rem;
  color: #94a3b8;
  pointer-events: none;
}

.filter-input {
  width: 100%;
  padding: 0.55rem 0.75rem 0.55rem 2.25rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  font-family: inherit;
  background: #ffffff;
  color: #1e293b;
  transition: border-color 150ms ease, box-shadow 150ms ease;
}

.filter-input:hover {
  border-color: #cbd5e1;
}

.filter-input:focus {
  outline: none;
  border-color: #4f46e5;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.15);
}

.table-wrapper {
  padding: 0;
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.data-table th {
  text-align: left;
  padding: 0.75rem 1.25rem;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.7rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #64748b;
  font-weight: 700;
}

.data-table td {
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid #f1f5f9;
  color: #1e293b;
}

.data-table tbody tr:last-child td {
  border-bottom: none;
}

.row-link {
  cursor: pointer;
  transition: background 150ms ease;
}

.row-link:hover {
  background: #f8fafc;
}

.cell-customer {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.cell-name {
  font-weight: 600;
}

.avatar {
  flex: 0 0 auto;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 999px;
  background: #eef2ff;
  color: #4f46e5;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 700;
}

.avatar-sm {
  width: 1.75rem;
  height: 1.75rem;
  font-size: 0.65rem;
}

.view-link {
  color: #4f46e5;
  font-weight: 600;
  text-decoration: none;
  border-radius: 4px;
  padding: 0.125rem 0.25rem;
  transition: color 150ms ease;
}

.view-link:hover {
  color: #3730a3;
  text-decoration: underline;
}

.view-link:focus-visible {
  outline: 2px solid #4f46e5;
  outline-offset: 1px;
}

.col-action {
  text-align: right;
  width: 4rem;
}

.client-code {
  background: #f1f5f9;
  border-radius: 4px;
  padding: 0.15rem 0.45rem;
  font-size: 0.8rem;
  color: #334155;
}

.pagination {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1.25rem;
}

.empty-card {
  text-align: center;
  padding: 3.5rem 2rem;
}

.empty-icon {
  width: 2.5rem;
  height: 2.5rem;
  color: #c7d2fe;
  margin-bottom: 0.75rem;
}

.empty-card h3 {
  margin: 0 0 0.375rem 0;
  font-size: 1rem;
  color: #0f172a;
}

.empty-card .muted {
  margin: 0 auto 1.25rem auto;
  max-width: 420px;
  font-size: 0.875rem;
}

/* ---------- Detail ---------- */

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  color: #4f46e5;
  font-size: 0.8125rem;
  font-weight: 600;
  text-decoration: none;
  margin-bottom: 1rem;
  border-radius: 4px;
  padding: 0.125rem 0.25rem;
  transition: color 150ms ease;
}

.back-link:hover {
  color: #3730a3;
}

.back-link:focus-visible {
  outline: 2px solid #4f46e5;
  outline-offset: 1px;
}

.back-icon {
  width: 1rem;
  height: 1rem;
}

.detail-header-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.detail-identity {
  display: flex;
  align-items: center;
  gap: 0.875rem;
}

.detail-name {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
}

.detail-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.25rem;
  font-size: 0.8125rem;
}

.meta-dot {
  width: 3px;
  height: 3px;
  border-radius: 999px;
  background: #cbd5e1;
}

.detail-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.confirm-text {
  font-size: 0.8125rem;
  color: #dc2626;
  font-weight: 600;
}

.derived-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.875rem;
}

.derived-tile {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  border: 1px solid #f1f5f9;
  border-radius: 8px;
  padding: 0.875rem 1rem;
  background: #fafbff;
}

.derived-icon {
  flex: 0 0 auto;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.derived-icon svg {
  width: 1.1rem;
  height: 1.1rem;
}

.icon-indigo { background: #eef2ff; color: #4f46e5; }
.icon-azure { background: #e0f2fe; color: #0284c7; }
.icon-aws { background: #fff7ed; color: #d97706; }
.icon-cloud { background: #f0fdf4; color: #16a34a; }

.derived-body {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.derived-label {
  font-size: 0.7rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #64748b;
  font-weight: 700;
}

.chip {
  display: inline-block;
  align-self: flex-start;
  border-radius: 999px;
  padding: 0.2rem 0.7rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: capitalize;
  white-space: nowrap;
}

.chip-new {
  background: #dcfce7;
  color: #15803d;
}

.chip-existing {
  background: #eef2ff;
  color: #4338ca;
}

.chip-na {
  background: #f1f5f9;
  color: #64748b;
  text-transform: none;
}

.task-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 0.375rem 1.25rem;
}

.task-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #1e293b;
}

.task-excluded {
  color: #94a3b8;
}

.task-icon {
  flex: 0 0 auto;
  width: 1.1rem;
  height: 1.1rem;
}

.task-icon-in {
  color: #16a34a;
}

.task-icon-out {
  color: #cbd5e1;
}

.detail-layout {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 1.25rem;
  align-items: start;
}

.panel-nav {
  position: sticky;
  top: 1.5rem;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  padding: 0.5rem;
  max-height: calc(100vh - 3rem);
  overflow-y: auto;
}

.panel-nav-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  background: transparent;
  border: none;
  border-radius: 6px;
  padding: 0.55rem 0.65rem;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  transition: background 150ms ease;
}

.panel-nav-item:hover {
  background: #f8fafc;
}

.panel-nav-item:focus-visible {
  outline: 2px solid #4f46e5;
  outline-offset: 1px;
}

.panel-nav-title {
  font-size: 0.8125rem;
  color: #475569;
  line-height: 1.3;
}

.panel-nav-active {
  background: #eef2ff;
}

.panel-nav-active:hover {
  background: #eef2ff;
}

.panel-nav-active .panel-nav-title {
  color: #0f172a;
  font-weight: 600;
}

.panel-nav-count {
  flex: 0 0 auto;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  color: #64748b;
  background: #f1f5f9;
  border-radius: 999px;
  padding: 0.12rem 0.5rem;
}

.panel-nav-active .panel-nav-count {
  background: #ffffff;
  color: #4f46e5;
}

.panel-content .card:last-child {
  margin-bottom: 0;
}

.section-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f1f5f9;
  margin-bottom: 0.25rem;
}

.section-card-header .card-title {
  margin: 0;
}

.answer-rows {
  display: flex;
  flex-direction: column;
}

.answer-row {
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 0 1.5rem;
  align-items: start;
  padding: 0.8rem 0;
  border-bottom: 1px solid #f1f5f9;
}

.answer-row:last-child {
  border-bottom: none;
  padding-bottom: 0.25rem;
}

.answer-label {
  font-size: 0.72rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
  font-weight: 700;
  padding-top: 0.25rem;
  line-height: 1.4;
}

.answer-value {
  min-width: 0;
}

.answer-chip-set {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.answer-chip {
  display: inline-block;
  background: #eef2ff;
  color: #4338ca;
  border: 1px solid #e0e7ff;
  border-radius: 999px;
  padding: 0.28rem 0.8rem;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.35;
}

.answer-text {
  margin: 0;
  font-size: 0.875rem;
  color: #1e293b;
  line-height: 1.55;
}

.answer-text-panel {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-left: 3px solid #c7d2fe;
  border-radius: 6px;
  padding: 0.75rem 1rem;
  white-space: pre-wrap;
  max-width: 75ch;
}

/* ---------- Common ---------- */

.primary-button,
.primary-button.as-link {
  background: #4f46e5;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 0.6rem 1.4rem;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  text-decoration: none;
  display: inline-block;
  transition: background 150ms ease, box-shadow 150ms ease;
}

.primary-button:hover,
.primary-button.as-link:hover {
  background: #4338ca;
  box-shadow: 0 2px 6px rgba(79, 70, 229, 0.3);
}

.primary-button:focus-visible {
  outline: 2px solid #4f46e5;
  outline-offset: 2px;
}

.secondary-button {
  background: #ffffff;
  color: #334155;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.5rem 1.1rem;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: border-color 150ms ease, background 150ms ease;
}

.secondary-button:hover:not(:disabled) {
  border-color: #cbd5e1;
  background: #f8fafc;
}

.secondary-button:focus-visible {
  outline: 2px solid #4f46e5;
  outline-offset: 2px;
}

.secondary-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.danger-button {
  background: #ffffff;
  color: #dc2626;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 0.5rem 1.1rem;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 150ms ease, border-color 150ms ease;
}

.danger-button:hover:not(:disabled) {
  background: #fef2f2;
  border-color: #fca5a5;
}

.danger-button:focus-visible {
  outline: 2px solid #dc2626;
  outline-offset: 2px;
}

.danger-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.loading-state {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  color: #64748b;
  padding: 2.5rem 0;
  font-size: 0.875rem;
}

.spinner {
  width: 1rem;
  height: 1rem;
  border: 2px solid #e2e8f0;
  border-top-color: #4f46e5;
  border-radius: 999px;
  animation: spin 750ms linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.muted {
  color: #64748b;
}

.small {
  font-size: 0.8rem;
  margin: 1rem 0 0 0;
}

.error-message {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin-bottom: 1.25rem;
  font-size: 0.875rem;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

@media (max-width: 900px) {
  .detail-layout {
    grid-template-columns: 1fr;
  }

  .panel-nav {
    position: static;
    display: flex;
    flex-wrap: nowrap;
    gap: 0.25rem;
    max-height: none;
    overflow-x: auto;
  }

  .panel-nav-item {
    flex: 0 0 auto;
    width: auto;
  }
}

@media (max-width: 700px) {
  .filters-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .search-wrap {
    width: 100%;
  }

  .answer-row {
    grid-template-columns: 1fr;
    gap: 0.375rem 0;
  }

  .answer-label {
    padding-top: 0;
  }
}
</style>
